import { useEffect, useState } from 'react'
import { useManualQuery } from 'graphql-hooks'

import * as logger from '../../../../services/logger'
import { getLocationsApproverTeamsAndLabels, getLocationsTeamsAndLabelsShort, getTeamsWithApprovers } from '../../../../graphql/custom-queries'
import { ITeamShort } from '@vacationtracker/shared/types/team'
import { IGetTeamsShort } from '../../../../types/teams'
import { IGetLabelsShort } from '../../../../types/labels'
import { IGetLocationsShort } from '../../../../types/locations'
import { IGetLocationsApproverTeamsAndLabelsShort, IGetLocationsTeamsAndLabelsShort } from '../../../../types/custom-queries'

export interface IUsersReferenceData {
  teams: ITeamShort[]
  locations: IGetLocationsShort[]
  labels: IGetLabelsShort[]
  approverUserIds: Set<string>
  isLoading: boolean
}

export interface IUseUsersReferenceDataParams {
  /**
   * Whether to narrow departments to the ones this user approves for. True only for approvers who
   * are not full admins; a pure location admin still sees every department.
   */
  approverScoped: boolean
  authUserId: string
}

/** The departments, locations and labels every users table needs to render and edit its cells. */
export function useUsersReferenceData({ approverScoped, authUserId }: IUseUsersReferenceDataParams): IUsersReferenceData {
  const [teams, setTeams] = useState<ITeamShort[]>([])
  const [locations, setLocations] = useState<IGetLocationsShort[]>([])
  const [labels, setLabels] = useState<IGetLabelsShort[]>([])
  const [approverUserIds, setApproverUserIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)

  const [getTeamsWithApproversQuery] = useManualQuery<{ getTeamListV2: { id: string; approvers: { id: string }[] }[] }>(getTeamsWithApprovers)
  const [getLocationsTeamsAndLabelsShortQuery] = useManualQuery<IGetLocationsTeamsAndLabelsShort>(getLocationsTeamsAndLabelsShort)
  const [getLocationsApproverTeamsAndLabelsQuery] = useManualQuery<IGetLocationsApproverTeamsAndLabelsShort>(getLocationsApproverTeamsAndLabels)

  useEffect(() => {
    getTeamsWithApproversQuery()
      .then((result) => {
        if (result.data) {
          setApproverUserIds(new Set(result.data.getTeamListV2.flatMap((team) => (team.approvers || []).map((approver) => approver.id))))
        }
      })
      .catch((error) => logger.warning(error))

    const fetch = async () => {
      try {
        let response
        if (approverScoped) {
          response = await getLocationsApproverTeamsAndLabelsQuery({ variables: { id: authUserId } })
          setTeams(response.data.getUser.approverToTeams as ITeamShort[])
        } else {
          response = await getLocationsTeamsAndLabelsShortQuery()
          setTeams(response.data.getTeamListV2 as IGetTeamsShort[])
        }
        setLocations(response.data.getLocationListV2 as IGetLocationsShort[])
        setLabels(response.data.getLabelListV2 as IGetLabelsShort[])
      } catch (error) {
        logger.warning({ errorType: 'ERROR FETCH USERS REFERENCE DATA', error })
      } finally {
        setIsLoading(false)
      }
    }

    fetch()
  }, [approverScoped, authUserId])

  return { teams, locations, labels, approverUserIds, isLoading }
}
